import React, { useState, useMemo } from 'react'
import type { User } from '@/services/userService'
import type { InstructorSplit, AssignInstructorInput, UpdateSplitInput } from '@/types/instructorSplit'
import type { Module } from '@/types/module'

// Format number to Indonesian Rupiah format (e.g., 1.500.000)
const formatRupiah = (value: number): string => {
  if (!value || value === 0) return ''
  return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')
}

// Parse formatted Rupiah string back to number
const parseRupiah = (value: string): number => {
  return parseInt(value.replace(/\./g, ''), 10) || 0
}

interface InstructorSplitFormProps {
  availableInstructors: User[]
  availableModules?: Module[]
  existingSplit?: InstructorSplit
  existingInstructorName?: string
  currentTotalPercentage: number
  onSubmit: (data: AssignInstructorInput[] | UpdateSplitInput) => void
  onCancel: () => void
  isLoading?: boolean
}

type CommissionType = 'percentage' | 'fixed'

interface SelectedInstructor {
  userId: string
  name: string
  email: string
  commissionType: CommissionType
  splitPercentage: number
  fixedAmount: number
  moduleAssignments: string[]
}

export const InstructorSplitForm: React.FC<InstructorSplitFormProps> = ({
  availableInstructors,
  availableModules = [],
  existingSplit,
  existingInstructorName,
  currentTotalPercentage,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const isEditMode = !!existingSplit

  // Determine initial commission type based on existing data
  const getInitialCommissionType = (): CommissionType => {
    if (existingSplit) {
      return existingSplit.fixedAmount > 0 ? 'fixed' : 'percentage'
    }
    return 'percentage'
  }

  // For edit mode - single instructor
  const [editCommissionType, setEditCommissionType] = useState<CommissionType>(getInitialCommissionType())
  const [editFormData, setEditFormData] = useState({
    splitPercentage: existingSplit?.percentage ?? 0,
    fixedAmount: existingSplit?.fixedAmount ?? 0,
  })
  const [editModuleAssignments, setEditModuleAssignments] = useState<string[]>(
    existingSplit?.moduleAssignments ?? []
  )

  // For add mode - multi-select
  const [selectedInstructors, setSelectedInstructors] = useState<SelectedInstructor[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [showDropdown, setShowDropdown] = useState(false)

  // Calculate available percentage
  const usedPercentage = selectedInstructors
    .filter((i) => i.commissionType === 'percentage')
    .reduce((sum, i) => sum + i.splitPercentage, 0)
  const availablePercentage = isEditMode
    ? 100 - currentTotalPercentage + (existingSplit?.percentage ?? 0)
    : 100 - currentTotalPercentage - usedPercentage

  // Filter instructors not yet selected
  const filteredInstructors = useMemo(() => {
    const selectedIds = new Set(selectedInstructors.map((i) => i.userId))
    return availableInstructors.filter(
      (instructor) =>
        !selectedIds.has(instructor.id) &&
        (instructor.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          instructor.email.toLowerCase().includes(searchQuery.toLowerCase()))
    )
  }, [availableInstructors, selectedInstructors, searchQuery])

  const handleSelectInstructor = (instructor: User) => {
    setSelectedInstructors([
      ...selectedInstructors,
      {
        userId: instructor.id,
        name: instructor.name,
        email: instructor.email,
        commissionType: 'percentage',
        splitPercentage: 0,
        fixedAmount: 0,
        moduleAssignments: [],
      },
    ])
    setSearchQuery('')
    setShowDropdown(false)
  }

  const handleRemoveSelected = (userId: string) => {
    setSelectedInstructors(selectedInstructors.filter((i) => i.userId !== userId))
  }

  const handleToggleModule = (userId: string, moduleId: string) => {
    setSelectedInstructors(
      selectedInstructors.map((i) => {
        if (i.userId !== userId) return i
        const hasModule = i.moduleAssignments.includes(moduleId)
        return {
          ...i,
          moduleAssignments: hasModule
            ? i.moduleAssignments.filter((m) => m !== moduleId)
            : [...i.moduleAssignments, moduleId],
        }
      })
    )
  }

  const handleToggleEditModule = (moduleId: string) => {
    setEditModuleAssignments((prev) =>
      prev.includes(moduleId) ? prev.filter((m) => m !== moduleId) : [...prev, moduleId]
    )
  }

  const handleUpdateSelected = (
    userId: string,
    field: 'commissionType' | 'splitPercentage' | 'fixedAmount',
    value: CommissionType | number
  ) => {
    setSelectedInstructors(
      selectedInstructors.map((i) => {
        if (i.userId !== userId) return i

        if (field === 'commissionType') {
          // Reset values when switching type
          return {
            ...i,
            commissionType: value as CommissionType,
            splitPercentage: 0,
            fixedAmount: 0,
          }
        }

        return { ...i, [field]: value }
      })
    )
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (isEditMode) {
      onSubmit({
        splitPercentage: editCommissionType === 'percentage' ? editFormData.splitPercentage : 0,
        fixedAmount: editCommissionType === 'fixed' ? editFormData.fixedAmount : 0,
        moduleAssignments: editModuleAssignments,
      } as UpdateSplitInput)
    } else {
      const data: AssignInstructorInput[] = selectedInstructors.map((i) => ({
        userId: i.userId,
        splitPercentage: i.commissionType === 'percentage' ? i.splitPercentage : 0,
        fixedAmount: i.commissionType === 'fixed' ? i.fixedAmount : 0,
        moduleAssignments: i.moduleAssignments,
      }))
      onSubmit(data)
    }
  }

  const isValid = isEditMode
    ? (editCommissionType === 'percentage' && editFormData.splitPercentage >= 0 && editFormData.splitPercentage <= availablePercentage) ||
      (editCommissionType === 'fixed' && editFormData.fixedAmount > 0)
    : selectedInstructors.length > 0 &&
      selectedInstructors.every((i) =>
        i.commissionType === 'percentage' ? i.splitPercentage >= 0 : i.fixedAmount > 0
      ) &&
      usedPercentage <= 100 - currentTotalPercentage

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {isEditMode ? (
        // Edit mode - single instructor
        <>
          <div className="p-3 rounded-lg bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10">
            <p className="text-sm text-gray-900 dark:text-white font-medium">{existingInstructorName}</p>
            <p className="text-xs text-gray-500 dark:text-text-muted">Editing commission split</p>
          </div>

          {/* Commission Type Toggle */}
          <div>
            <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-2">
              Commission Type
            </label>
            <div className="flex p-1 bg-gray-200/50 dark:bg-black/20 rounded-xl">
              <button
                type="button"
                onClick={() => {
                  setEditCommissionType('percentage')
                  setEditFormData({ ...editFormData, fixedAmount: 0 })
                }}
                className={`flex-1 py-2 px-4 rounded-lg text-sm font-bold transition-all duration-200 ${
                  editCommissionType === 'percentage'
                    ? 'bg-white dark:bg-surface-card text-primary shadow-sm ring-1 ring-black/5 dark:ring-white/10'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                }`}
              >
                Percentage (%)
              </button>
              <button
                type="button"
                onClick={() => {
                  setEditCommissionType('fixed')
                  setEditFormData({ ...editFormData, splitPercentage: 0 })
                }}
                className={`flex-1 py-2 px-4 rounded-lg text-sm font-bold transition-all duration-200 ${
                  editCommissionType === 'fixed'
                    ? 'bg-white dark:bg-surface-card text-primary shadow-sm ring-1 ring-black/5 dark:ring-white/10'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                }`}
              >
                Fixed Amount (Rp)
              </button>
            </div>
          </div>

          {/* Value Input based on type */}
          {editCommissionType === 'percentage' ? (
            <div>
              <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-1">
                Split Percentage <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={editFormData.splitPercentage}
                  onChange={(e) =>
                    setEditFormData({ ...editFormData, splitPercentage: Number(e.target.value) })
                  }
                  min="0"
                  max={availablePercentage}
                  className="w-full px-3 py-2 bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400">%</span>
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-text-muted">Available: {availablePercentage}%</p>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-1">
                Fixed Amount <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400">Rp</span>
                <input
                  type="text"
                  value={formatRupiah(editFormData.fixedAmount)}
                  onChange={(e) => {
                    const rawValue = e.target.value.replace(/\./g, '')
                    if (/^\d*$/.test(rawValue)) {
                      setEditFormData({ ...editFormData, fixedAmount: parseRupiah(rawValue) })
                    }
                  }}
                  placeholder="0"
                  className="w-full pl-10 pr-3 py-2 bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-text-muted">Fixed amount per enrollment</p>
            </div>
          )}

          {/* Module Assignments for Edit Mode */}
          {availableModules.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-2">
                Assigned Modules
              </label>
              <p className="text-xs text-gray-500 dark:text-text-muted mb-3">
                Select which modules this instructor can edit
              </p>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {availableModules.map((module) => (
                  <label
                    key={module.id}
                    className="flex items-center gap-3 p-2 rounded-lg bg-gray-100 dark:bg-white/5 hover:bg-gray-200 dark:hover:bg-white/10 cursor-pointer transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={editModuleAssignments.includes(module.id)}
                      onChange={() => handleToggleEditModule(module.id)}
                      className="w-4 h-4 rounded border-gray-600 text-primary focus:ring-primary bg-gray-700"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-900 dark:text-white truncate">{module.title}</p>
                      <p className="text-xs text-gray-500 dark:text-text-muted">
                        Order: {module.orderIndex + 1}
                      </p>
                    </div>
                  </label>
                ))}
              </div>
              <p className="mt-2 text-xs text-gray-500 dark:text-text-muted">
                Selected: {editModuleAssignments.length} of {availableModules.length} modules
              </p>
            </div>
          )}
        </>
      ) : (
        // Add mode - multi-select
        <>
          {/* Instructor Search/Select */}
          <div>
            <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-1">
              Select Instructors <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value)
                  setShowDropdown(true)
                }}
                onFocus={() => setShowDropdown(true)}
                placeholder="Search instructor by name or email..."
                className="w-full px-3 py-2 bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 material-symbols-outlined text-gray-500 dark:text-gray-400">
                search
              </span>

              {/* Dropdown */}
              {showDropdown && filteredInstructors.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-gray-50 dark:bg-surface-dark border border-gray-200 dark:border-white/10 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {filteredInstructors.map((instructor) => (
                    <button
                      key={instructor.id}
                      type="button"
                      onClick={() => handleSelectInstructor(instructor)}
                      className="w-full px-3 py-2 text-left hover:bg-gray-200 dark:hover:bg-white/10 flex items-center gap-2"
                    >
                      <span className="material-symbols-outlined text-primary text-lg">person</span>
                      <div>
                        <p className="text-sm text-gray-900 dark:text-white">{instructor.name}</p>
                        <p className="text-xs text-gray-500 dark:text-text-muted">{instructor.email}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {showDropdown && filteredInstructors.length === 0 && searchQuery && (
              <p className="mt-1 text-xs text-gray-500 dark:text-text-muted">No instructors found</p>
            )}
          </div>

          {/* Selected Instructors */}
          {selectedInstructors.length > 0 && (
            <div className="space-y-3">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-300">
                Selected ({selectedInstructors.length})
              </p>
              {selectedInstructors.map((instructor) => (
                <div
                  key={instructor.userId}
                  className="p-3 rounded-lg bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
                >
                  <div className="flex items-start justify-between mb-3 border-b border-gray-200 dark:border-white/10 pb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded bg-primary/10 flex items-center justify-center text-primary">
                        <span className="material-symbols-outlined text-sm">person</span>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-sm text-gray-900 dark:text-white font-bold">{instructor.name || instructor.email}</span>
                        {instructor.name && <span className="text-xs text-gray-500 dark:text-gray-400">{instructor.email}</span>}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveSelected(instructor.userId)}
                      className="p-1.5 hover:bg-red-500/10 rounded-lg transition-colors"
                    >
                      <span className="material-symbols-outlined text-red-500 text-lg">delete</span>
                    </button>
                  </div>

                  {/* Commission Type Toggle */}
                  <div className="flex p-1 bg-gray-200/50 dark:bg-black/20 rounded-lg mb-3">
                    <button
                      type="button"
                      onClick={() => handleUpdateSelected(instructor.userId, 'commissionType', 'percentage')}
                      className={`flex-1 py-1.5 px-2 rounded-md text-xs font-bold transition-all duration-200 ${
                        instructor.commissionType === 'percentage'
                          ? 'bg-white dark:bg-surface-card text-primary shadow-sm ring-1 ring-black/5 dark:ring-white/10'
                          : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-black/5 dark:hover:bg-white/5'
                      }`}
                    >
                      Percentage
                    </button>
                    <button
                      type="button"
                      onClick={() => handleUpdateSelected(instructor.userId, 'commissionType', 'fixed')}
                      className={`flex-1 py-1.5 px-2 rounded-md text-xs font-bold transition-all duration-200 ${
                        instructor.commissionType === 'fixed'
                          ? 'bg-white dark:bg-surface-card text-primary shadow-sm ring-1 ring-black/5 dark:ring-white/10'
                          : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-black/5 dark:hover:bg-white/5'
                      }`}
                    >
                      Fixed (Rp)
                    </button>
                  </div>

                  {/* Value Input */}
                  {instructor.commissionType === 'percentage' ? (
                    <div className="relative">
                      <input
                        type="number"
                        value={instructor.splitPercentage}
                        onChange={(e) =>
                          handleUpdateSelected(instructor.userId, 'splitPercentage', Number(e.target.value))
                        }
                        min="0"
                        max="100"
                        placeholder="0"
                        className="w-full px-2 py-1.5 bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-lg text-gray-900 dark:text-white text-sm"
                      />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 text-sm">%</span>
                    </div>
                  ) : (
                    <div className="relative">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 text-sm">Rp</span>
                      <input
                        type="text"
                        value={formatRupiah(instructor.fixedAmount)}
                        onChange={(e) => {
                          const rawValue = e.target.value.replace(/\./g, '')
                          if (/^\d*$/.test(rawValue)) {
                            handleUpdateSelected(instructor.userId, 'fixedAmount', parseRupiah(rawValue))
                          }
                        }}
                        placeholder="0"
                        className="w-full pl-8 pr-2 py-1.5 bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-lg text-gray-900 dark:text-white text-sm"
                      />
                    </div>
                  )}

                  {/* Module Assignments for Add Mode */}
                  {availableModules.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-600">
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Assign Modules:</p>
                      <div className="flex flex-wrap gap-2">
                        {availableModules.map((module) => (
                          <button
                            key={module.id}
                            type="button"
                            onClick={() => handleToggleModule(instructor.userId, module.id)}
                            className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                              instructor.moduleAssignments.includes(module.id)
                                ? 'bg-primary text-white'
                                : 'bg-gray-100 dark:bg-white/5 text-gray-500 dark:text-text-muted hover:bg-gray-200 dark:hover:bg-white/10'
                            }`}
                          >
                            {module.title}
                          </button>
                        ))}
                      </div>
                      <p className="text-xs text-gray-500 dark:text-text-muted mt-1">
                        {instructor.moduleAssignments.length} module(s) selected
                      </p>
                    </div>
                  )}
                </div>
              ))}

              {/* Total indicator - only show if any percentage-based */}
              {selectedInstructors.some((i) => i.commissionType === 'percentage') && (
                <div
                  className={`flex items-center justify-between px-3 py-2 rounded-lg border ${
                    usedPercentage <= 100 - currentTotalPercentage
                      ? 'bg-green-500/10 border-green-500/30'
                      : 'bg-red-500/10 border-red-500/30'
                  }`}
                >
                  <span className="text-sm text-gray-500 dark:text-text-muted">New percentage splits total:</span>
                  <span
                    className={`font-bold ${
                      usedPercentage <= 100 - currentTotalPercentage ? 'text-green-400' : 'text-red-400'
                    }`}
                  >
                    {usedPercentage}%
                  </span>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-4 border-t border-gray-600">
        <button
          type="button"
          onClick={onCancel}
          disabled={isLoading}
          className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isLoading || !isValid}
          className="px-4 py-2 bg-primary text-white rounded-lg font-bold disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? 'Saving...' : isEditMode ? 'Update Split' : 'Assign Instructors'}
        </button>
      </div>
    </form>
  )
}

export default InstructorSplitForm
